// Content CMS — curriculum tree navigator. docs/next_session.md "content depth"
// / CLAUDE.md §14. Subjects → Tracks → Units → Lessons, expandable, with status
// badges and create/reorder at each level. Click a lesson to open its editor.
//
// Staff tool, English chrome. All text fields are translation KEYS (authors fill
// the strings in the Translations panel).

import { Badge, Button, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { createContext, useContext, useState } from 'react';

// A tree is the right shape here, so there is no DataTable — but the header
// and the loading/empty/error states come from the console system (M7c / P6).
// The content-status badge stays local: `content_status` has its own
// draft/in_review/published vocabulary, defined below.
import { ConfirmDialog, EmptyState, ErrorState, Icon, PageHeader, RowActions } from '~/components';
import { ApiError } from '~/lib/api';
import {
  GRADES,
  LANGUAGES,
  TRACK_VARIANTS,
  createLesson,
  createSubject,
  createTrack,
  createUnit,
  deleteLesson,
  deleteSubject,
  deleteTrack,
  deleteUnit,
  getTree,
  reorderLessons,
  reorderUnits,
  updateUnit,
  type ContentStatus,
  type TreeSubject,
  type TreeTrack,
  type TreeUnit,
} from '~/lib/content';

export const Route = createFileRoute('/_authed/content/')({
  component: ContentTreePage,
});

const TREE_KEY = ['content', 'tree'];

/** What the shared delete dialog is currently asking about. */
interface PendingDelete {
  kind: string;
  run: () => void;
}

// One dialog for the whole tree rather than one per node: the tree can hold
// hundreds of nodes and mounting a dialog in each is pure waste. Each node
// reads this context itself — a module-level helper that called the hook would
// be calling it outside a component.
const DeleteContext = createContext<(p: PendingDelete) => void>(() => {});
function useDeleteConfirm(): (p: PendingDelete) => void {
  return useContext(DeleteContext);
}

function ContentTreePage() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: TREE_KEY });
  const treeQuery = useQuery({
    queryKey: TREE_KEY,
    queryFn: ({ signal }) => getTree(signal),
    staleTime: 5_000,
  });

  const [addingSubject, setAddingSubject] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  return (
    <DeleteContext.Provider value={setPendingDelete}>
      <div className="mx-auto max-w-5xl">
        <PageHeader
          title="Curriculum"
          description="The curriculum tree — subjects, tracks, units, lessons. Text fields are translation keys; author the strings in Translations."
          actions={
            <Button onClick={() => setAddingSubject((v) => !v)}>
              <Icon name="plus" className="h-4 w-4" />
              New subject
            </Button>
          }
        />

        {addingSubject && (
          <CreateSubjectForm
            onDone={() => {
              setAddingSubject(false);
              invalidate();
            }}
          />
        )}

        {treeQuery.isLoading ? (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-xl bg-admin-100" />
            ))}
          </div>
        ) : treeQuery.error ? (
          <ErrorState
            error={treeQuery.error}
            onRetry={() => void treeQuery.refetch()}
            className="mt-6 rounded-xl border border-admin-200 bg-white"
          />
        ) : treeQuery.data && treeQuery.data.length > 0 ? (
          <div className="mt-6 space-y-4">
            {treeQuery.data.map((subject) => (
              <SubjectNode key={subject.id} subject={subject} onMutated={invalidate} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="book"
            title="No subjects yet"
            description="A subject is the top of the curriculum tree — start there, then add tracks, units and lessons."
            action={<Button onClick={() => setAddingSubject(true)}>New subject</Button>}
            className="mt-6 rounded-xl border border-admin-200 bg-white"
          />
        )}

        <ConfirmDialog
          open={pendingDelete != null}
          onOpenChange={(open) => !open && setPendingDelete(null)}
          title={`Delete this ${pendingDelete?.kind ?? 'item'}`}
          description={
            <>
              Everything nested under this {pendingDelete?.kind ?? 'item'} goes with it, and this
              cannot be undone from here.
            </>
          }
          confirmLabel={`Delete ${pendingDelete?.kind ?? 'item'}`}
          tone="danger"
          onConfirm={() => {
            pendingDelete?.run();
            setPendingDelete(null);
          }}
        />
      </div>
    </DeleteContext.Provider>
  );
}

// ─── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ContentStatus }) {
  const tone: Parameters<typeof Badge>[0]['tone'] =
    status === 'published' ? 'success' : status === 'in_review' ? 'warning' : 'neutral';
  return <Badge tone={tone}>{status.replace('_', ' ')}</Badge>;
}

// ─── Subject node ─────────────────────────────────────────────────────────

function SubjectNode({ subject, onMutated }: { subject: TreeSubject; onMutated: () => void }) {
  const setConfirming = useDeleteConfirm();
  const [open, setOpen] = useState(true);
  const [addingTrack, setAddingTrack] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteSubject(subject.id),
    onSuccess: onMutated,
    onError: (e) => alert(errMsg(e)),
  });

  return (
    <section className="overflow-hidden rounded-2xl border border-burgundy-500/10 bg-white">
      <div className="flex items-center gap-3 px-5 py-4">
        <Chevron open={open} onClick={() => setOpen((v) => !v)} />
        <span className="text-lg">{subject.icon ? '📘' : '📚'}</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ink-900">{subject.slug}</p>
          <p className="font-mono text-xs text-ink-500">{subject.nameKey}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setAddingTrack((v) => !v)}>
          + Track
        </Button>
        <DeleteBtn
          label="subject"
          onClick={() => setConfirming({ kind: 'subject', run: () => del.mutate() })}
        />
      </div>

      {open && (
        <div className="border-t border-burgundy-500/10 bg-cream-50/40 px-5 py-4">
          {addingTrack && (
            <CreateTrackForm
              subjectId={subject.id}
              onDone={() => {
                setAddingTrack(false);
                onMutated();
              }}
            />
          )}
          {subject.tracks.length === 0 ? (
            <p className="py-2 text-sm text-ink-500">No tracks yet.</p>
          ) : (
            <div className="space-y-3">
              {subject.tracks.map((track) => (
                <TrackNode key={track.id} track={track} onMutated={onMutated} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Track node ─────────────────────────────────────────────────────────

function TrackNode({ track, onMutated }: { track: TreeTrack; onMutated: () => void }) {
  const setConfirming = useDeleteConfirm();
  const [open, setOpen] = useState(true);
  const [addingUnit, setAddingUnit] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteTrack(track.id),
    onSuccess: onMutated,
    onError: (e) => alert(errMsg(e)),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderUnits(track.id, ids),
    onSuccess: onMutated,
  });

  return (
    <div className="rounded-xl border border-burgundy-500/10 bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <Chevron open={open} onClick={() => setOpen((v) => !v)} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink-900">
            {track.grade} · {track.variant} · {track.language}
          </p>
          <p className="font-mono text-xs text-ink-500">{track.titleKey}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setAddingUnit((v) => !v)}>
          + Unit
        </Button>
        <DeleteBtn
          label="track"
          onClick={() => setConfirming({ kind: 'track', run: () => del.mutate() })}
        />
      </div>

      {open && (
        <div className="border-t border-burgundy-500/10 px-4 py-3">
          {addingUnit && (
            <CreateUnitForm
              trackId={track.id}
              onDone={() => {
                setAddingUnit(false);
                onMutated();
              }}
            />
          )}
          {track.units.length === 0 ? (
            <p className="py-1 text-sm text-ink-500">No units yet.</p>
          ) : (
            <ol className="space-y-2">
              {track.units.map((unit, idx) => (
                <UnitNode
                  key={unit.id}
                  unit={unit}
                  index={idx}
                  total={track.units.length}
                  onMutated={onMutated}
                  onMove={(dir) => {
                    const ids = track.units.map((u) => u.id);
                    const j = dir === 'up' ? idx - 1 : idx + 1;
                    if (j < 0 || j >= ids.length) return;
                    [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
                    reorder.mutate(ids);
                  }}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Unit node ─────────────────────────────────────────────────────────

function UnitNode({
  unit,
  index,
  total,
  onMutated,
  onMove,
}: {
  unit: TreeUnit;
  index: number;
  total: number;
  onMutated: () => void;
  onMove: (dir: 'up' | 'down') => void;
}) {
  const setConfirming = useDeleteConfirm();
  const [open, setOpen] = useState(false);
  const [addingLesson, setAddingLesson] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteUnit(unit.id),
    onSuccess: onMutated,
    onError: (e) => alert(errMsg(e)),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderLessons(unit.id, ids),
    onSuccess: onMutated,
  });
  // Free-tier boundary: toggling flips whether this unit is playable without a
  // subscription (families-only MVP). Free units are entitled to everyone.
  const toggleFree = useMutation({
    mutationFn: () => updateUnit(unit.id, { isFree: !unit.isFree }),
    onSuccess: onMutated,
    onError: (e) => alert(errMsg(e)),
  });

  return (
    <li className="rounded-lg border border-burgundy-500/10 bg-cream-50/60">
      <div className="flex items-center gap-2 px-3 py-2">
        <Chevron open={open} onClick={() => setOpen((v) => !v)} small />
        <MoveButtons index={index} total={total} onMove={onMove} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink-900">{unit.titleKey}</p>
        </div>
        <button
          type="button"
          onClick={() => toggleFree.mutate()}
          disabled={toggleFree.isPending}
          title={
            unit.isFree
              ? 'Free — playable without a subscription. Click to make it paid.'
              : 'Paid — needs a subscription. Click to make it free.'
          }
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 transition-colors disabled:opacity-50 ${
            unit.isFree
              ? 'bg-leaf/15 text-leaf ring-leaf/30 hover:bg-leaf/25'
              : 'bg-ink-500/10 text-ink-500 ring-ink-500/20 hover:bg-ink-500/20'
          }`}
        >
          {unit.isFree ? 'Free' : 'Paid'}
        </button>
        <StatusBadge status={unit.status} />
        <Button variant="ghost" size="sm" onClick={() => setAddingLesson((v) => !v)}>
          + Lesson
        </Button>
        <DeleteBtn
          label="unit"
          onClick={() => setConfirming({ kind: 'unit', run: () => del.mutate() })}
        />
      </div>

      {open && (
        <div className="border-t border-burgundy-500/10 px-3 py-2">
          {addingLesson && (
            <CreateLessonForm
              unitId={unit.id}
              onDone={() => {
                setAddingLesson(false);
                onMutated();
              }}
            />
          )}
          {unit.lessons.length === 0 ? (
            <p className="py-1 text-sm text-ink-500">No lessons yet.</p>
          ) : (
            <ol className="space-y-1">
              {unit.lessons.map((lesson, idx) => (
                <li
                  key={lesson.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white"
                >
                  <MoveButtons
                    index={idx}
                    total={unit.lessons.length}
                    onMove={(dir) => {
                      const ids = unit.lessons.map((l) => l.id);
                      const j = dir === 'up' ? idx - 1 : idx + 1;
                      if (j < 0 || j >= ids.length) return;
                      [ids[idx], ids[j]] = [ids[j]!, ids[idx]!];
                      reorder.mutate(ids);
                    }}
                  />
                  <Link
                    to="/content/lessons/$id"
                    params={{ id: lesson.id }}
                    className="min-w-0 flex-1 truncate text-sm text-burgundy-600 hover:underline"
                  >
                    {lesson.titleKey}
                  </Link>
                  <span className="text-xs text-ink-500">{lesson.estMinutes}m</span>
                  <StatusBadge status={lesson.status} />
                  <DeleteBtn
                    label="lesson"
                    onClick={() =>
                      setConfirming({
                        kind: 'lesson',
                        run: () => void deleteLesson(lesson.id).then(onMutated),
                      })
                    }
                  />
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

// ─── Create forms ───────────────────────────────────────────────────────────

function CreateSubjectForm({ onDone }: { onDone: () => void }) {
  const [slug, setSlug] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => createSubject({ slug: slug.trim(), nameKey: nameKey.trim() }),
    onSuccess: onDone,
    onError: (e) => setErr(errMsg(e)),
  });
  return (
    <CreateShell
      title="New subject"
      onSubmit={() => mut.mutate()}
      loading={mut.isPending}
      error={err}
    >
      <LabeledInput label="Slug" value={slug} onChange={setSlug} placeholder="arabic, math" mono />
      <LabeledInput
        label="Name key"
        value={nameKey}
        onChange={setNameKey}
        placeholder="subject.arabic.name"
        mono
      />
    </CreateShell>
  );
}

function CreateTrackForm({ subjectId, onDone }: { subjectId: string; onDone: () => void }) {
  const [grade, setGrade] = useState<string>(GRADES[0]);
  const [variant, setVariant] = useState<string>(TRACK_VARIANTS[0]);
  const [language, setLanguage] = useState<string>(LANGUAGES[0]);
  const [titleKey, setTitleKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      createTrack({ subjectId, grade, variant, language, titleKey: titleKey.trim() }),
    onSuccess: onDone,
    onError: (e) => setErr(errMsg(e)),
  });
  return (
    <CreateShell
      title="New track"
      onSubmit={() => mut.mutate()}
      loading={mut.isPending}
      error={err}
    >
      <div className="grid grid-cols-3 gap-2">
        <SelectField label="Grade" value={grade} onChange={setGrade} options={GRADES} />
        <SelectField
          label="Variant"
          value={variant}
          onChange={setVariant}
          options={TRACK_VARIANTS}
        />
        <SelectField label="Language" value={language} onChange={setLanguage} options={LANGUAGES} />
      </div>
      <LabeledInput
        label="Title key"
        value={titleKey}
        onChange={setTitleKey}
        placeholder="track.x.title"
        mono
      />
    </CreateShell>
  );
}

function CreateUnitForm({ trackId, onDone }: { trackId: string; onDone: () => void }) {
  const [titleKey, setTitleKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => createUnit({ trackId, titleKey: titleKey.trim() }),
    onSuccess: onDone,
    onError: (e) => setErr(errMsg(e)),
  });
  return (
    <CreateShell title="New unit" onSubmit={() => mut.mutate()} loading={mut.isPending} error={err}>
      <LabeledInput
        label="Title key"
        value={titleKey}
        onChange={setTitleKey}
        placeholder="unit.x.title"
        mono
      />
    </CreateShell>
  );
}

function CreateLessonForm({ unitId, onDone }: { unitId: string; onDone: () => void }) {
  const [titleKey, setTitleKey] = useState('');
  const [estMinutes, setEstMinutes] = useState('10');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      createLesson({ unitId, titleKey: titleKey.trim(), estMinutes: Number(estMinutes) || 10 }),
    onSuccess: onDone,
    onError: (e) => setErr(errMsg(e)),
  });
  return (
    <CreateShell
      title="New lesson"
      onSubmit={() => mut.mutate()}
      loading={mut.isPending}
      error={err}
    >
      <div className="grid grid-cols-[1fr_6rem] gap-2">
        <LabeledInput
          label="Title key"
          value={titleKey}
          onChange={setTitleKey}
          placeholder="lesson.x.title"
          mono
        />
        <LabeledInput label="Minutes" value={estMinutes} onChange={setEstMinutes} />
      </div>
    </CreateShell>
  );
}

// ─── Shared small UI ─────────────────────────────────────────────────────────

function CreateShell({
  title,
  onSubmit,
  loading,
  error,
  children,
}: {
  title: string;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <form
      className="mb-3 rounded-xl border border-burgundy-500/15 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <p className="mb-2 text-sm font-semibold text-ink-900">{title}</p>
      <div className="space-y-2">{children}</div>
      {error && <p className="mt-2 text-sm text-heart">{error}</p>}
      <div className="mt-3 flex justify-end">
        <Button type="submit" size="sm" loading={loading}>
          Create
        </Button>
      </div>
    </form>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="block text-xs font-medium text-ink-700">
      {label}
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 ${mono ? 'font-mono text-xs' : ''}`}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  return (
    <label className="block text-xs font-medium text-ink-700">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-2xl border border-burgundy-500/20 bg-white px-3 py-2 text-sm text-ink-900"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function Chevron({
  open,
  onClick,
  small,
}: {
  open: boolean;
  onClick: () => void;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? 'Collapse' : 'Expand'}
      aria-expanded={open}
      className={`shrink-0 rounded-md text-ink-500 transition-transform hover:text-ink-900 ${
        small ? 'text-xs' : ''
      } ${open ? 'rotate-90' : ''}`}
    >
      ▶
    </button>
  );
}

function MoveButtons({
  index,
  total,
  onMove,
}: {
  index: number;
  total: number;
  onMove: (dir: 'up' | 'down') => void;
}) {
  return (
    <span className="flex flex-col">
      <button
        type="button"
        onClick={() => onMove('up')}
        disabled={index === 0}
        aria-label="Move up"
        className="text-[10px] leading-none text-ink-500 hover:text-ink-900 disabled:opacity-25"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={() => onMove('down')}
        disabled={index === total - 1}
        aria-label="Move down"
        className="text-[10px] leading-none text-ink-500 hover:text-ink-900 disabled:opacity-25"
      >
        ▼
      </button>
    </span>
  );
}

/**
 * The tree's delete control, in the console's unified row-action shape.
 *
 * 🔴 This replaced an emoji button plus a native `window.confirm()`. A native
 * confirm is unstyled, untestable through the DOM, and dismissible with a stray
 * Enter — a poor guard on a delete that cascades through a curriculum subtree.
 */
function DeleteBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <RowActions
      label={label}
      actions={[
        {
          id: 'delete',
          label: `Delete ${label}`,
          icon: 'trash',
          destructive: true,
          onSelect: onClick,
        },
      ]}
    />
  );
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
